/**
 * The Dashboard's data hook. Given the active filter set, it fetches every
 * rollup surface the screen needs — the KPI summary (with period-over-period
 * deltas), the volume timeseries, the five dimension breakdowns, the top
 * automation opportunities and the review backlog — as one coordinated load, and
 * collapses them into a single discriminated state the screen renders from.
 *
 * A filter change re-keys the effect, which re-fetches everything and aborts the
 * previous request in flight. `reload()` re-runs the same load (the error-state
 * Retry).
 *
 * It also asks how many documents are still mid-pipeline. Analytics come from
 * `rollups_daily`, which the events worker folds a document into only once it is
 * classified — so immediately after an ingestion the rollups are legitimately
 * behind the documents. Knowing the in-flight count lets the screen say so, and
 * lets the hook re-load itself on a timer until the pipeline drains. Those
 * refreshes are quiet — they swap the data in place rather than dropping the
 * whole dashboard back to skeletons — and the loop ends by itself.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '../../api/errors';
import type {
  AnalyticsBreakdown,
  AnalyticsSummary,
  AnalyticsTimeseries,
  Opportunity,
} from '../../api/types';
import { useSession } from '../../auth/session';
import { usePolling } from '../../lib/hooks';

import {
  DIMENSION_KEYS,
  serializeFilters,
  toApiFilter,
  withDimension,
  type DashboardFilters,
  type DimensionKey,
} from './filters';

export type DimensionName = 'type' | 'location' | 'scanner' | 'source' | 'confidence';

const DIMENSIONS: readonly DimensionName[] = ['type', 'location', 'scanner', 'source', 'confidence'];

export interface ReviewBacklog {
  count: number;
  oldestCreatedAt: string | null;
}

export interface DashboardData {
  summary: AnalyticsSummary;
  timeseries: AnalyticsTimeseries;
  breakdowns: Record<DimensionName, AnalyticsBreakdown>;
  /**
   * The filter-chip options: each dimension's breakdown under every *other*
   * active filter but not its own, so picking a Type still lists every other
   * type to switch to (instead of only the one already chosen).
   */
  facets: Record<DimensionKey, AnalyticsBreakdown>;
  opportunities: Opportunity[];
  review: ReviewBacklog;
}

export type DashboardState =
  | { status: 'loading' }
  | { status: 'error'; traceId: string | null; reload: () => void }
  | {
      status: 'ready';
      data: DashboardData;
      reload: () => void;
      /**
       * Documents ingested but not yet through classification, so not yet in the
       * rollups these charts read. `> 0` means the dashboard is polling.
       */
      processing: number;
    };

/** How long after a settled load to re-check, while documents are in flight. */
export const LIVE_POLL_MS = 5000;

function traceOf(error: unknown): string | null {
  if (error instanceof ApiError && error.problem !== null) {
    const problem = error.problem;
    const id = problem.correlation_id ?? problem.trace_id ?? problem.traceId;
    if (typeof id === 'string') return id;
  }
  return null;
}

/** True only for the AbortController-driven cancel of a superseded/unmounted load. */
function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export function useDashboard(filters: DashboardFilters): DashboardState {
  const { api } = useSession();
  const [state, setState] = useState<DashboardState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);
  // Set only by the poll, cleared by the load it belongs to: it decides whether
  // this run shows skeletons or swaps the charts' data underneath them.
  const quiet = useRef(false);

  const reload = useCallback(() => {
    quiet.current = false;
    setNonce((n) => n + 1);
  }, []);

  const refresh = useCallback(() => {
    quiet.current = true;
    setNonce((n) => n + 1);
  }, []);

  // Re-key on the serialised filters so any filter change re-fetches everything.
  const key = serializeFilters(filters).toString();

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const apiFilter = toApiFilter(filters);
    const silent = quiet.current;
    quiet.current = false;
    if (!silent) setState({ status: 'loading' });

    void (async () => {
      try {
        const [
          summary,
          timeseries,
          type,
          location,
          scanner,
          source,
          confidence,
          opportunities,
          review,
          inFlight,
        ] = await Promise.all([
          api.analytics.summary(apiFilter, signal),
          api.analytics.timeseries({ ...apiFilter, granularity: 'day' }, signal),
          api.analytics.breakdown('type', apiFilter, signal),
          api.analytics.breakdown('location', apiFilter, signal),
          api.analytics.breakdown('scanner', apiFilter, signal),
          api.analytics.breakdown('source', apiFilter, signal),
          api.analytics.breakdown('confidence', apiFilter, signal),
          api.automation.list({ limit: 3 }, signal),
          api.review.list({ limit: 1 }, signal),
          // The in-flight count is a nicety, not part of the dashboard's
          // contract: a principal with analytics but not documents:read still
          // gets the whole screen, just without the live indicator.
          api.documents
            .list({ skip: 0, limit: 1, status: 'processing' }, signal)
            .then((page) => page.total)
            .catch(() => 0),
        ]);
        if (signal.aborted) return;
        const breakdowns = { type, location, scanner, source, confidence } as Record<
          DimensionName,
          AnalyticsBreakdown
        >;
        // Only a dimension that is itself filtered needs a second request; an
        // unset one's breakdown is already its facet.
        const facetEntries = await Promise.all(
          DIMENSION_KEYS.map(async (dim) =>
            filters[dim] === undefined
              ? ([dim, breakdowns[dim]] as const)
              : ([
                  dim,
                  await api.analytics.breakdown(
                    dim,
                    toApiFilter(withDimension(filters, dim, undefined)),
                    signal,
                  ),
                ] as const),
          ),
        );
        const facets = Object.fromEntries(facetEntries) as Record<DimensionKey, AnalyticsBreakdown>;
        setState({
          status: 'ready',
          reload,
          processing: inFlight,
          data: {
            summary,
            timeseries,
            breakdowns,
            facets,
            opportunities: opportunities.items,
            review: {
              count: review.total,
              oldestCreatedAt: review.items[0]?.created_at ?? null,
            },
          },
        });
      } catch (error) {
        if (signal.aborted || isAbort(error)) return;
        // A quiet refresh that fails leaves the last good dashboard up: seconds-old
        // numbers beat replacing a working screen with an error over one dropped poll.
        if (silent) return;
        setState({ status: 'error', traceId: traceOf(error), reload });
      }
    })();

    return () => {
      controller.abort();
    };
    // `key` captures the filter identity; `nonce` forces a manual reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, key, nonce, reload]);

  // `state` is the restart key: a new object per settled load re-arms the timer.
  usePolling(state.status === 'ready' && state.processing > 0, LIVE_POLL_MS, state, refresh);

  return state;
}

/** The dimension breakdowns, in the order the screen renders them. */
export { DIMENSIONS };
